// Human sign-off: approve the video or override the verdict. Recorded as a
// new entry in the history; the AI result is kept. Approving is one click
// only when the current verdict is already PASS; passing anything else needs
// a written reason. The decision names the result it was made on, so it
// can't silently land on a newer run.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import { VERDICT } from './qcFormat';
import { errorText } from './qcClient';
import { canDecideQc } from './qcAccess';
import { Button, C } from './ui';

// Staff only on the server. `canDecide` defaults to the signed-in user's role,
// and sample mode always allows it so the demo stays fully clickable.
export default function DecisionBox({ videoId, check, client, sample = false, track = () => {}, onDecided, canDecide = sample || canDecideQc() }) {
    const currentVerdict = check.verdict;
    const [overriding, setOverriding] = useState(false);
    const [verdict, setVerdict] = useState('PASS');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const open = (preset) => { setVerdict(preset); setOverriding(true); };

    const decide = async (chosen, text) => {
        setSaving(true);
        try {
            await client.decide(videoId, { verdict: chosen, note: text, check_id: check.id });
            toast.success(chosen === 'PASS' ? 'Marked as approved' : `Verdict set to ${VERDICT[chosen].label}`);
            track('video_qc_decision', { verdict: chosen, override: chosen !== currentVerdict });
            setOverriding(false);
            setNote('');
            onDecided();
        } catch (err) {
            if (err?.error?.code === 'stale_result') {
                toast.error('A newer result came in while you were reviewing. Showing it now.');
                onDecided();
            } else {
                toast.error(errorText(err));
            }
        } finally {
            setSaving(false);
        }
    };

    const hint = sample
        ? 'Sample data: decisions show here but are not saved.'
        : 'Your decision is saved with your name and the time. The AI result stays in the history.';

    if (!canDecide) {
        return (
            <div role="group" aria-label="Your review" style={{ background: C.soft, border: `1px solid ${C.border}`, borderRadius: 16, padding: 18, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span aria-hidden="true" style={{ width: 38, height: 38, borderRadius: 10, background: '#fff', border: `1px solid ${C.border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <GavelRoundedIcon style={{ fontSize: 20, color: C.muted }} />
                </span>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Review by the Influencer.in team</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>
                        The AI suggests a verdict and our team makes the final call. Only staff can approve or override a result.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div role="group" aria-label="Your review" style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 16, padding: 18, boxShadow: '0 10px 26px -20px rgba(17,25,54,0.5)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <span aria-hidden="true" style={{ width: 38, height: 38, borderRadius: 10, background: C.goldSoft, border: '1px solid #f5e3b0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <GavelRoundedIcon style={{ fontSize: 20, color: '#a16207' }} />
                    </span>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Your review</div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>The AI suggests a verdict. You make the final call. {hint}</div>
                    </div>
                </div>
                {!overriding && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {currentVerdict === 'PASS' ? (
                            <Button variant="success" disabled={saving} onClick={() => decide('PASS', '')}>✓ Approve</Button>
                        ) : (
                            <Button disabled={saving} onClick={() => open('PASS')}>✓ Approve anyway…</Button>
                        )}
                        <Button disabled={saving} onClick={() => open(currentVerdict === 'PASS' ? 'FAIL' : 'PASS')}>Override verdict…</Button>
                    </div>
                )}
            </div>
            {overriding && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.line}` }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {Object.entries(VERDICT).map(([key, v]) => (
                            <button key={key} type="button" className="vqc-chip" onClick={() => setVerdict(key)} aria-pressed={verdict === key}
                                style={{ display: 'inline-flex', alignItems: 'center', boxSizing: 'border-box', minHeight: 32, padding: '0 14px', borderRadius: 999, fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer', background: verdict === key ? v.bg : '#fff', color: v.color, border: `1px solid ${verdict === key ? v.color : C.border}` }}>
                                {v.icon} {v.label}
                            </button>
                        ))}
                    </div>
                    <textarea aria-label="Reason" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                        placeholder="Why? e.g. Brand approved the 71% stat on call"
                        style={{ width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button small onClick={() => setOverriding(false)} disabled={saving}>Cancel</Button>
                        <Button small variant="primary" onClick={() => decide(verdict, note.trim())} disabled={saving || !note.trim()}
                            title={note.trim() ? undefined : 'Add a reason for the override'}>
                            {saving ? 'Saving…' : 'Save decision'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
